import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Lock, Crown, Eye, EyeOff, X, KeyRound, ShieldAlert } from 'lucide-react';
import { ADMIN_PASSWORD } from '../utils/saveManager';
import { playSound } from '../audio';

interface AdminPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AdminPasswordModal: React.FC<AdminPasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = password.trim();

    if (clean === ADMIN_PASSWORD) {
      playSound('correct');
      setError(null);
      setPassword('');
      onSuccess();
    } else {
      playSound('wrong');
      setError('Mot de passe incorrect. Accès refusé.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  const handleClose = () => {
    playSound('click');
    setPassword('');
    setError(null);
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 15 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
            x: isShaking ? [-8, 8, -6, 6, -3, 3, 0] : 0,
          }}
          exit={{ opacity: 0, scale: 0.9, y: 10 }}
          transition={{
            type: isShaking ? 'keyframes' : 'spring',
            duration: isShaking ? 0.4 : undefined,
            stiffness: 300,
            damping: 25,
          }}
          className="relative w-full max-w-md bg-stone-950 border-2 border-amber-500/70 rounded-2xl shadow-2xl p-5 sm:p-6 text-white flex flex-col gap-4 overflow-hidden"
        >
          {/* Subtle Ambient Golden Glow */}
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-36 h-36 bg-amber-500/15 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-36 h-36 bg-red-500/10 rounded-full blur-2xl pointer-events-none" />

          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-stone-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-600 to-yellow-700 flex items-center justify-center text-amber-100 shadow-md border border-amber-400/50 shrink-0">
              <Crown className="w-6 h-6 text-amber-200 animate-pulse" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400 font-black flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Sécurité & Administration
              </div>
              <h2 className="text-base sm:text-lg font-black font-cinzel text-amber-100 truncate">
                Accès Mode Admin
              </h2>
            </div>
          </div>

          <p className="text-xs text-stone-300 leading-relaxed font-sans">
            Veuillez entrer le mot de passe secret pour déverrouiller les fonctionnalités d'administration, le Studio et la gestion du système.
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold text-amber-300/90 uppercase tracking-wider flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" />
                Mot de passe Administrateur
              </label>

              <div className="relative flex items-center">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="Entrez le mot de passe..."
                  autoFocus
                  className={`w-full bg-stone-900/90 border-2 ${
                    error
                      ? 'border-red-500 focus:border-red-400 ring-1 ring-red-500/50'
                      : 'border-amber-500/50 focus:border-amber-400'
                  } rounded-xl px-4 py-3 pr-11 text-sm sm:text-base text-white placeholder:text-stone-500 font-mono tracking-wide focus:outline-none transition-all shadow-inner`}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 p-1.5 text-stone-400 hover:text-amber-200 transition-colors cursor-pointer"
                  title={showPassword ? 'Masquer' : 'Afficher'}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-1.5 text-xs text-red-400 font-bold mt-1 animate-fadeIn">
                  <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-2.5 px-3 bg-stone-800 hover:bg-stone-700 text-stone-300 hover:text-white rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors cursor-pointer border border-stone-700 active:scale-95"
              >
                Annuler
              </button>

              <button
                type="submit"
                className="flex-1 py-2.5 px-3 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-400 text-stone-950 font-black text-xs sm:text-sm uppercase tracking-wider rounded-xl transition-all shadow-md active:translate-y-0.5 cursor-pointer border border-amber-400/80 flex items-center justify-center gap-1.5"
              >
                <span>Déverrouiller</span>
                <span>👑</span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
